import { ConsentKeys, type Consents } from '@carvana/core-consent';
import { useNativeLinkInterceptor } from '@carvana/core-native-messaging';
import Checkbox from '@carvana/ds-checkbox';
import React, { useEffect, useRef } from 'react';
import { PageEventContextProvider } from '|/context';
import useEventHelper from '|/hooks/use-event-helper';
import { ReviewSummaryResponse } from '|/services/purchase-api/types';
import styles from './consents-section.module.css';

const FireAgreementsEvents: React.FC = () => {
  const { sendLoadEvent } = useEventHelper();
  const loadFiredRef = useRef(false);

  useEffect(() => {
    if (loadFiredRef.current) return;
    loadFiredRef.current = true;
    sendLoadEvent(true);
  }, [sendLoadEvent]);

  return null;
};

interface ConsentProps {
  consentHtml: Consents;
  shouldDisplayNRSF: boolean;
  shouldDisplayMarylandNRSF: boolean;
  isCarvanaFinanced: boolean;
  summary: ReviewSummaryResponse;
  updateAgreement: (key: ConsentKeys | string, value: boolean) => void;
  agreementStates: Record<ConsentKeys | string, boolean>;
  previouslyAdoptedConsents?: Set<string>;
}

const ConsentsSection = ({
  consentHtml,
  shouldDisplayNRSF,
  shouldDisplayMarylandNRSF,
  isCarvanaFinanced,
  summary,
  agreementStates,
  updateAgreement,
  previouslyAdoptedConsents = new Set()
}: ConsentProps) => {
  const nrsfConfirmation = agreementStates[ConsentKeys.NRSF] || false;
  const mdNrsfConfirmation = agreementStates[ConsentKeys.MarylandNRSF] || false;
  const jumioConfirmation = agreementStates[ConsentKeys.Jumio] || false;
  const incomeConfirmation = agreementStates.income || false;
  const hardPullConfirmation = agreementStates[ConsentKeys.HardPull] || false;

  const containerId = 'review-order-consents';
  useNativeLinkInterceptor({
    containerId: summary ? containerId : '',
    source: 'purchase'
  });

  if (summary === null) {
    return null;
  }

  const requireHardPull = isCarvanaFinanced && !previouslyAdoptedConsents.has(ConsentKeys.HardPull);
  const requireIncome = isCarvanaFinanced && !previouslyAdoptedConsents.has('income');
  const requireJumio =
    !!consentHtml[ConsentKeys.Jumio]?.html && !previouslyAdoptedConsents.has(ConsentKeys.Jumio);
  const requireNrsf = shouldDisplayNRSF && !previouslyAdoptedConsents.has(ConsentKeys.NRSF);
  const requireMdNrsf =
    shouldDisplayMarylandNRSF && !previouslyAdoptedConsents.has(ConsentKeys.MarylandNRSF);

  const allAccepted =
    (!requireHardPull || hardPullConfirmation) &&
    (!requireIncome || incomeConfirmation) &&
    (!requireJumio || jumioConfirmation) &&
    (!requireNrsf || nrsfConfirmation) &&
    (!requireMdNrsf || mdNrsfConfirmation);

  return (
    <PageEventContextProvider
      value={{ pageName: 'Final Details', pageSubName: 'Agreements', sectionName: 'Final Details' }}
    >
      <FireAgreementsEvents />
      <section
        data-testid="pp_review-order_agreements"
        id={containerId}
        className={styles.container}
      >
        <header className={styles.heading}>
          <h3>Agreements</h3>
          <p className={styles.subtext}>
            Please review and accept the agreement(s) below. You can still make changes at any time
            before placing your order.
          </p>
        </header>

        <div className={styles.consentCheckboxes}>
          {isCarvanaFinanced && !previouslyAdoptedConsents.has(ConsentKeys.HardPull) && (
            <Checkbox
              size="small"
              id="hardPullConfirmation"
              name="hardPullConfirmationCheckbox"
              data-testid="toggle-hardpull-authorize"
              className={styles.checkboxBlock}
              checked={hardPullConfirmation}
              onChange={() => {
                updateAgreement(ConsentKeys.HardPull, !hardPullConfirmation);
              }}
              label={
                <span
                  dangerouslySetInnerHTML={{
                    __html: consentHtml[ConsentKeys.HardPull].html
                  }}
                />
              }
            />
          )}

          {isCarvanaFinanced && !previouslyAdoptedConsents.has('income') && (
            <Checkbox
              size="small"
              id="incomeConfirmation"
              name="employmentStatusCheckbox"
              data-testid="toggle-authorize"
              className={styles.checkboxBlock}
              checked={incomeConfirmation}
              onChange={() => {
                updateAgreement('income', !incomeConfirmation);
              }}
              label={
                <span>
                  The income information I provided is accurate and reflects any recent changes to
                  my employment status or hours. I understand that Carvana will verify my income.
                </span>
              }
            />
          )}

          {consentHtml[ConsentKeys.Jumio].html &&
            !previouslyAdoptedConsents.has(ConsentKeys.Jumio) && (
              <Checkbox
                size="small"
                id="jumioConfirmation"
                name="employmentStatusCheckbox"
                data-testid="toggle-jumio-authorize"
                className={styles.checkboxBlock}
                checked={jumioConfirmation}
                onChange={() => {
                  updateAgreement(ConsentKeys.Jumio, !jumioConfirmation);
                }}
                label={
                  <span
                    dangerouslySetInnerHTML={{
                      __html: consentHtml[ConsentKeys.Jumio].html
                    }}
                  />
                }
              />
            )}

          {shouldDisplayNRSF && !previouslyAdoptedConsents.has(ConsentKeys.NRSF) && (
            <Checkbox
              size="small"
              id="nrsfConfirmation"
              name="employmentStatusCheckbox"
              data-testid="toggle-nrsf-authorize"
              className={styles.checkboxBlock}
              checked={nrsfConfirmation}
              onChange={() => {
                updateAgreement(ConsentKeys.NRSF, !nrsfConfirmation);
              }}
              label={
                <span
                  dangerouslySetInnerHTML={{
                    __html: consentHtml[ConsentKeys.NRSF].html
                  }}
                />
              }
            />
          )}

          {shouldDisplayMarylandNRSF &&
            !previouslyAdoptedConsents.has(ConsentKeys.MarylandNRSF) && (
              <Checkbox
                size="small"
                id="mdNrsfConfirmation"
                name="employmentStatusCheckbox"
                data-testid="toggle-md-nrsf-authorize"
                className={styles.checkboxBlock}
                checked={mdNrsfConfirmation}
                onChange={() => {
                  updateAgreement(ConsentKeys.MarylandNRSF, !mdNrsfConfirmation);
                }}
                label={
                  <span
                    dangerouslySetInnerHTML={{
                      __html: consentHtml[ConsentKeys.MarylandNRSF].html
                    }}
                  />
                }
              />
            )}
        </div>
      </section>
    </PageEventContextProvider>
  );
};

export default ConsentsSection;
